import { ref, readonly, type Ref, onUnmounted, getCurrentInstance } from 'vue'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH'

type RequestOptions = {
  body?: unknown
  params?: Record<string, string | number | boolean | null | undefined>
  headers?: Record<string, string>
  signal?: AbortSignal
  // Per-request deadline. Defaults to DEFAULT_TIMEOUT_MS; pass null to opt a
  // request out entirely (no known caller needs that today).
  timeoutMs?: number | null
}

type FetchError = {
  message: string
  status: number | null
  endpoint: string
  body?: unknown
  timedOut?: boolean
  stack?: string
}

// Catch-all deadline so a hung socket (flaky mobile networks, in-app browser
// proxies) can never park a caller forever — awaited event-processor chains
// block navigation on these requests, so "eventually" must be bounded.
const DEFAULT_TIMEOUT_MS = 30_000

type UseFetch = {
  loading: Readonly<Ref<boolean>>
  error: Readonly<Ref<FetchError | null>>
  get: <T>(url: string, options?: Omit<RequestOptions, 'body'>) => Promise<T>
  post: <T>(url: string, options?: RequestOptions) => Promise<T>
  put: <T>(url: string, options?: RequestOptions) => Promise<T>
  patch: <T>(url: string, options?: RequestOptions) => Promise<T>
  abort: () => void
}

function buildUrl(url: string, params?: RequestOptions['params']): string {
  if (!params) return url

  const filtered = Object.entries(params).filter(([, value]) => value != null) as [string, string][]
  if (!filtered.length) return url

  return `${url}?${new URLSearchParams(filtered).toString()}`
}

export function useFetch(baseUrl = ''): UseFetch {
  const loading = ref(false)
  const error = ref<FetchError | null>(null)
  let controller: AbortController | null = null

  // Reset default state, when component dismounted, that uses this hook (composable).
  // Only register when called inside a setup() — outside (e.g. from a watch
  // callback after an await), getCurrentInstance() is null and Vue warns.
  // Callers in that scenario are responsible for invoking abort() themselves.
  if (getCurrentInstance()) {
    onUnmounted(() => {
      controller?.abort()
      loading.value = false
      error.value = null
    })
  }

  function abort() {
    controller?.abort()
  }

  async function request<T>(
    method: HttpMethod,
    url: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const { body, params, headers = {}, signal, timeoutMs = DEFAULT_TIMEOUT_MS } = options

    // Keep a local handle: `controller` is shared module state overwritten by
    // every request, so the timeout below must abort THIS request's
    // controller — aborting `controller` could kill a newer in-flight call.
    const ctrl = new AbortController()
    controller = ctrl

    // Manual AbortController + setTimeout rather than AbortSignal.timeout()/
    // AbortSignal.any(): built pipes run in old Android WebViews and Meta
    // in-app browsers where those statics may not exist, and that cohort is
    // exactly where hung requests are most common.
    let timedOut = false
    const timer =
      timeoutMs != null
        ? setTimeout(() => {
            timedOut = true
            ctrl.abort()
          }, timeoutMs)
        : null
    // Forward an optional caller-supplied signal into ours (poor-man's
    // AbortSignal.any) so both the caller and the timeout can abort.
    if (signal) {
      if (signal.aborted) ctrl.abort()
      else signal.addEventListener('abort', () => ctrl.abort(), { once: true })
    }

    loading.value = true
    error.value = null

    const fullUrl = buildUrl(`${baseUrl}${url}`, params)

    try {
      const response = await fetch(fullUrl, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: ctrl.signal,
      })

      if (!response.ok) {
        // Capture the response body so callers / console diagnostics can see
        // *why* the server rejected the request — `statusText` is empty over
        // HTTP/2, which is why error messages used to surface as "Request
        // failed: " with no detail.
        let parsedBody: unknown
        let bodyText = ''
        try {
          bodyText = await response.text()
          parsedBody = bodyText ? JSON.parse(bodyText) : undefined
        } catch {
          parsedBody = bodyText || undefined
        }
        const summary =
          response.statusText ||
          (typeof parsedBody === 'object' && parsedBody !== null && 'message' in parsedBody
            ? String((parsedBody as { message: unknown }).message)
            : bodyText.slice(0, 200))
        const message = `Request failed (${response.status}): ${summary || 'no body'}`
        const err: FetchError = {
          message,
          status: response.status,
          endpoint: fullUrl,
          body: parsedBody,
          stack: new Error(message).stack,
        }
        error.value = err

        throw err
      }

      // Handle empty responses (204 No Content etc.)
      const text = await response.text()
      if (!text) {
        return null as unknown as T
      }

      return JSON.parse(text) as T
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // A deadline abort is a real failure the caller must see (it decides
        // whether to retry / proceed); only deliberate aborts (unmount,
        // caller signal) stay silent.
        if (timedOut) {
          error.value = {
            message: `Request timed out after ${timeoutMs}ms`,
            status: null,
            endpoint: fullUrl,
            timedOut: true,
            stack: (err as Error).stack,
          }
          throw error.value
        }
        error.value = null

        throw err
      }

      if (!error.value) {
        error.value = {
          message: (err as Error)?.message ?? 'Unknown error',
          status: null,
          endpoint: fullUrl,
          stack: (err as Error)?.stack,
        }
      }

      throw error.value
    } finally {
      if (timer != null) clearTimeout(timer)
      loading.value = false
    }
  }

  return {
    loading: readonly(loading),
    error: readonly(error),
    get: (url, options) => request('GET', url, options),
    post: (url, options) => request('POST', url, options),
    put: (url, options) => request('PUT', url, options),
    patch: (url, options) => request('PATCH', url, options),
    abort,
  }
}
